'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type React from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { isDev } from '~/utils';
import { createGeneralSurveySteps } from './generalSurveySteps';
import { type SurveyEvent, useSurveyTrigger } from './useSurveyTrigger';
type SurveyProviderProps = {
  surveyType: 'general';
  children: React.ReactNode;
};
type SurveyState = {
  surveyType: 'general';
  steps: ReturnType<typeof createGeneralSurveySteps>;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  happyPath: string;
  setHappyPath: React.Dispatch<React.SetStateAction<string>>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  surveyEvents: SurveyEvent[];
  showingFollowUps: boolean;
  setShowingFollowUps: React.Dispatch<React.SetStateAction<boolean>>;
};
export const GeneralSurveyContext = createContext<SurveyState | null>(null);
export function SurveyProvider(t0) {
  const $ = _c(13);
  const {
    surveyType,
    children
  } = t0;
  const t = useTranslations();
  const [step, setStep] = useState(0);
  const [happyPath, setHappyPath] = useState("happy");
  const [isOpen, setIsOpen] = useState(false);
  const [showingFollowUps, setShowingFollowUps] = useState(false);
  const {
    surveyEvents
  } = useSurveyTrigger(`${surveyType}Survey`);
  let t1;
  if ($[0] !== t) {
    t1 = createGeneralSurveySteps(t);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const steps = t1;
  let t2;
  if ($[2] !== happyPath || $[3] !== isOpen || $[4] !== showingFollowUps || $[5] !== step || $[6] !== steps || $[7] !== surveyEvents || $[8] !== surveyType) {
    t2 = {
      surveyType,
      steps,
      step,
      setStep,
      happyPath,
      setHappyPath,
      isOpen,
      setIsOpen,
      surveyEvents,
      showingFollowUps,
      setShowingFollowUps
    };
    $[2] = happyPath;
    $[3] = isOpen;
    $[4] = showingFollowUps;
    $[5] = step;
    $[6] = steps;
    $[7] = surveyEvents;
    $[8] = surveyType;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const value = t2;
  let t3;
  if ($[10] !== children || $[11] !== value) {
    t3 = <GeneralSurveyContext.Provider value={value}>{children}</GeneralSurveyContext.Provider>;
    $[10] = children;
    $[11] = value;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
export function useSurvey(surveyType) {
  const $ = _c(4);
  const generalContext = useContext(GeneralSurveyContext);
  const context = generalContext;
  if (!context) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = [];
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = [];
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== surveyType) {
      t2 = {
        isOpen: false,
        step: 0,
        steps: t0,
        happyPath: "happy",
        surveyEvents: t1,
        showingFollowUps: false,
        setIsOpen: () => {
          if (isDev()) {
            console.warn(`If you're trying to use a ${surveyType} survey, you need to wrap a parent component in a SurveyProvider`);
          }
        },
        setShowingFollowUps: _temp,
        setHappyPath: _temp2,
        setStep: _temp3
      };
      $[2] = surveyType;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  return context;
}
function _temp3() {}
function _temp2() {}
function _temp() {}